import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Chip, Button, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import apiClient from '../../config/axios';
import { translatePaymentType, getPaymentTypeColor } from '../../utils/paymentTypeTranslator';
import { exportFinanceToCSV } from '../../utils/exportCSV/exportFinance';
import ModalExpense from '../../components/ModalExpense';
import ModalConfirm from '../../components/ModalConfirm';
import ModalResult from '../../components/ModalResult';
import FinanceBalance from '../../components/FinanceBalance';
import getConfig from '../../config/app-config';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { FileDownload } from '@mui/icons-material';

dayjs.locale('it');

const Finance = () => {
  const config = getConfig();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState('');
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [resultSeverity, setResultSeverity] = useState('info');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);

  useEffect(() => {
    fetchFinanceData();
  }, []);

  const fetchFinanceData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/finance');
      setData(response.data);
      // console.log('response', response.data);
    } catch (err) {
      setError('Errore nel caricamento dei dati finanziari');
      console.error('Error fetching finance data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate dynamic height based on number of rows
  // const calculateGridHeight = () => {
  //   const rowHeight = 52; // Default row height
  //   const headerHeight = 56; // Header height
  //   const minHeight = 400; // Minimum height
  //   const maxHeight = 800; // Maximum height

  //   if (!data || data.length === 0) {
  //     return minHeight;
  //   }

  //   const calculatedHeight = headerHeight + (data.length * rowHeight) + 20; // +20 for padding
  //   return Math.min(Math.max(calculatedHeight, minHeight), maxHeight);
  // };

  const exportToCSV = () => {
    exportFinanceToCSV(data);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Tesseramento':
        return 'primary';
      case 'nuovo corso':
        return 'success';
      case 'rinnovo corso':
        return 'warning';
      case 'Spesa':
        return 'error';
      default:
        return 'default';
    }
  };

  const getMethodColor = (method) => {
    return getPaymentTypeColor(method);
  };

  const getRegisteredByColor = (role) => {
    switch (role) {
      case 'admin':
        return 'primary';
      case 'teacher':
        return 'warning';
      case 'member':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleNoteClick = (note) => {
    setSelectedNote(note);
    setNoteModalOpen(true);
  };

  const handleNoteModalClose = () => {
    setNoteModalOpen(false);
    setSelectedNote('');
  };

  const handleAddExpense = () => {
    setExpenseModalOpen(true);
  };

  const handleExpenseModalClose = () => {
    setExpenseModalOpen(false);
  };

  const handleExpenseSuccess = (expense) => {
    setResultMessage(`Spesa "${expense.name}" aggiunta con successo!`);
    setResultSeverity('success');
    setResultModalOpen(true);
    // Refresh data to show the new expense
    fetchFinanceData();
  };

  const handleResultModalClose = () => {
    setResultModalOpen(false);
  };

  const handleDeleteExpenseRequest = (expenseId) => {
    if (!expenseId) return;
    setExpenseToDelete(expenseId);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setExpenseToDelete(null);
  };

  const handleDeleteExpense = async () => {
    if (!expenseToDelete) return;

    try {
      await apiClient.delete(`/api/finance/expenses/${expenseToDelete}`);
      setResultMessage('Spesa eliminata con successo.');
      setResultSeverity('success');
      setResultModalOpen(true);
      fetchFinanceData();
    } catch (err) {
      console.error('Error deleting expense:', err);
      setResultMessage("Errore durante l'eliminazione della spesa.");
      setResultSeverity('error');
      setResultModalOpen(true);
    } finally {
      handleConfirmClose();
    }
  };

  const columns = [
    {
      field: 'type',
      headerName: 'Tipo',
      width: 150,
      renderCell: (params) => <Chip label={params.value.charAt(0).toUpperCase() + params.value.slice(1)} color={getTypeColor(params.value)} size='small' />,
    },
    {
      field: 'name',
      headerName: 'Nome',
      width: 135,
      renderCell: (params) => {
        return params.value || 'N/A';
      },
    },

    {
      field: 'createdAt',
      headerName: 'Data',
      width: 120,
      sortable: true,
      valueFormatter: (params) => {
        if (!params?.value) {
          return '-';
        }
        return dayjs(params.value).format('DD-MMM-YY');
      },
      sortComparator: (v1, v2) => {
        // Custom sort comparator for dates
        const date1 = dayjs(v1);
        const date2 = dayjs(v2);
        return date1.diff(date2);
      },
    },
    {
      field: 'method',
      headerName: 'Metodo',
      // width: 120,
      renderCell: (params) => {
        if (!params.value) {
          return (
            <Typography variant='body2' color='text.secondary'>
              -
            </Typography>
          );
        }
        return <Chip label={translatePaymentType(params.value)} color={getMethodColor(params.value)} size='small' />;
      },
    },
    {
      field: 'amount',
      headerName: 'Importo',
      // width: 120,
      valueFormatter: (params) => {
        // console.log('params', params);
        return `${params}€`;
      },
    },
    {
      field: 'fullName',
      headerName: 'Utente',
      flex: 1.5,
      minWidth: 180,
      resizable: true,
      renderCell: (params) => {
        // console.log('params', params);
        // gestisci caso expense che non ha user
        if (!params.row.user) {
          return (
            <Typography variant='body2' color='text.secondary'>
              -
            </Typography>
          );
        }
        const name = params.row.user.name || '';
        const surname = params.row.user.surname || '';
        const fullName = `${name} ${surname}`.trim() || 'N/A';
        const email = params.row.user.email || '';
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='body2'>{fullName}</Typography>
            <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.75rem' }}>
              {email}
            </Typography>
          </Box>
        );
      },
      valueGetter: (_, row) => {
        // console.log('row', row);
        const name = row.user?.name || '';
        const surname = row.user?.surname || '';
        const fullName = `${name} ${surname}`.trim() || 'N/A';
        const email = row.user?.email || '';
        return `${fullName} ${email}`.toLowerCase();
      },
    },
    // {
    //   field: 'stripeId',
    //   headerName: 'Stripe ID',
    //   // width: 200,
    //   renderCell: (params) => {
    //     return params.value || 'N/A';
    //   }
    // },

    {
      field: 'registeredBy',
      headerName: 'Registrato Da',
      width: 150,
      renderCell: (params) => {
        if (!params.value) return 'N/A';

        const { name, surname, roles } = params.value;
        const initials = `${name?.charAt(0) || ''}${surname?.charAt(0) || ''}`.toUpperCase();
        const mainRole = roles?.[0] || 'member';

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Chip label={initials} color={getRegisteredByColor(mainRole)} size='small' sx={{ mb: 0.5 }} />
            <Typography variant='caption' color='text.secondary'>
              {mainRole}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'note',
      headerName: 'Note',
      flex: 1,
      minWidth: 120,
      resizable: true,
      renderCell: (params) => {
        const note = params.value;
        if (!note) {
          return (
            <Typography variant='body2' color='text.secondary'>
              -
            </Typography>
          );
        }

        const preview = note.length > 30 ? `${note.substring(0, 30)}...` : note;

        return (
          <Tooltip title={note} arrow placement='top'>
            <Box
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                },
                p: 0.5,
                borderRadius: 1,
              }}
              onClick={() => handleNoteClick(note)}
            >
              <Typography
                variant='body2'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  color: '#1565c0',
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'primary.dark',
                    textDecoration: 'underline',
                  },
                }}
              >
                {preview}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 90,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        if (params.row.type !== 'Spesa') {
          return (
            <Typography variant='body2' color='text.secondary'>
              -
            </Typography>
          );
        }

        return (
          <Tooltip title='Elimina spesa'>
            <IconButton
              color='error'
              size='small'
              onClick={() => handleDeleteExpenseRequest(params.row.id)}
            >
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant='h6' gutterBottom>
        Gestione Finanziaria
      </Typography>

      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Entrate: iscrizioni corsi, rinnovo iscrizioni corsi o tesseramenti. Uscite: spese.
      </Typography>

      <FinanceBalance data={data} />

      <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'flex-end' }}>
        <Button variant='outlined' color='primary' startIcon={<FileDownload />}onClick={exportToCSV} disabled={!data || data.length === 0} size='small'>
          Esporta CSV
        </Button>
        {config.WITH_EXPENSES && (
          <Button variant='contained' color='primary' onClick={handleAddExpense} size='small'>
            Aggiungi Spesa
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper
        // elevation={3}
        sx={{
          p: 2,
          // bgcolor: 'background.paper', // tipicamente bianco
        }}
      >
        <DataGrid
          rows={data}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
            sorting: {
              sortModel: [{ field: 'createdAt', sort: 'desc' }], // Most recent first by default
            },
          }}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
          getRowId={(row) => row.id}
          sx={{
            backgroundColor: 'transparent',
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
              backgroundColor: '#fff',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#fff',
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#fff',
            },
          }}
        />
      </Paper>
      <Dialog open={noteModalOpen} onClose={handleNoteModalClose} maxWidth='sm' fullWidth disableEscapeKeyDown>
        <DialogTitle>Note Pagamento</DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {selectedNote}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNoteModalClose} variant='contained' color='primary'>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <ModalExpense open={expenseModalOpen} onClose={handleExpenseModalClose} onSuccess={handleExpenseSuccess} />

      <ModalConfirm
        open={confirmOpen}
        title='Elimina Spesa'
        message='Sei sicuro di voler eliminare definitivamente questa spesa?'
        onConfirm={handleDeleteExpense}
        onCancel={handleConfirmClose}
      />

      <ModalResult open={resultModalOpen} onClose={handleResultModalClose} message={resultMessage} severity={resultSeverity} title={resultSeverity === 'success' ? 'Successo' : 'Errore'} />
    </Box>
  );
};

export default Finance;
